import { useCallback, useEffect, useRef, useState } from "react";
import { instrumentHeroImage } from "@/lib/hero-perf";
import { useHeroDebug } from "@/lib/hero-debug";
import { HeroDebugOverlay } from "@/components/site/HeroDebugOverlay";

type Props = {
  src: string;
  /**
   * Accessible description of the portrait. Required for meaningful imagery —
   * this is what a screen-reader user hears in place of the photo, so describe
   * the person and the framing, not the page's keywords. Canonical strings for
   * the site's portraits live in `src/content/image-alt.ts`.
   *
   * Pass `decorative` when the same portrait is repeated purely as art and the
   * surrounding copy already conveys it; the image is then hidden from the
   * accessibility tree instead of announcing duplicate text.
   */
  alt: string;
  /** Hide the portrait from assistive tech (alt=""). Default false. */
  decorative?: boolean;
  /** Optional single-URL AVIF source (back-compat with older call sites). */
  avifSrc?: string;
  /** Optional single-URL WebP source (back-compat with older call sites). */
  webpSrc?: string;
  /**
   * Multi-width srcset strings (produced by `vite-imagetools` `as=srcset`).
   * Preferred over `avifSrc`/`webpSrc` because they let the browser pick the
   * smallest variant that fits the rendered slot at the current DPR.
   */
  avifSrcSet?: string;
  webpSrcSet?: string;
  jpgSrcSet?: string;
  /**
   * Optional PNG rung. Rarely worth shipping alongside JPEG, but if the
   * source asset is a PNG (screenshots, UI, transparent art) it's the
   * safest universal fallback and will be tried after JPEG.
   */
  pngSrc?: string;
  pngSrcSet?: string;
  /**
   * `sizes` attribute for the responsive srcset. Must reflect the actual
   * layout width of the image at each breakpoint or the browser will pick a
   * too-large (wasted bytes) or too-small (blurry) candidate.
   */
  sizes?: string;
  /**
   * Optional fallback image (e.g. a smaller JPEG or a static asset from /public)
   * used if the primary <picture> sources fail to load. When omitted, a branded
   * gradient placeholder with initials is rendered instead.
   */
  fallbackSrc?: string;
  /**
   * Optional tiny (≤32px wide) low-quality image placeholder shown blurred
   * behind the hero while the full asset loads. Prevents the shimmer flash on
   * slow connections without adding perceptible bytes to the LCP budget.
   * Crop anchor is intentionally reused so the blur-up doesn't shift the
   * face position when the sharp image resolves.
   */
  placeholderSrc?: string;
  /**
   * Optional per-instance override for the crop anchor. When omitted the
   * portrait reads the site-wide `--hero-anchor-x` / `--hero-anchor-y` CSS
   * variables defined in `index.css`, so designers can retune the face
   * position from theme config without touching this component.
   */
  anchorX?: string;
  anchorY?: string;
  /**
   * LCP hint. When `true` (default) the image loads eagerly with
   * `fetchpriority="high"` — correct for the homepage hero. Set to `false`
   * for non-LCP instances (e.g. QA harness, below-the-fold placements) so
   * the browser lazy-loads and deprioritises the fetch on mobile networks.
   */
  priority?: boolean;
  /**
   * Responsive crop zoom. Any CSS number expression (usually a custom
   * property) multiplied into the portrait's scale so the subject fills the
   * frame consistently as the frame aspect changes across breakpoints.
   */
  zoom?: string;
  /** Label shown in the QA crop-debug overlay (Shift+H / ?heroDebug=1). */
  debugLabel?: string;

};

/**
 * Ordered list of format rungs the <picture> will try, best → safest.
 * The onError handler advances one rung at a time, so an AVIF file that
 * 404s (or decodes to garbage on a device with buggy AVIF support) still
 * downgrades cleanly to WebP → JPEG → PNG → author fallback → branded
 * card, instead of jumping straight to the failure state.
 */
type FormatRung = "avif" | "webp" | "jpeg" | "png" | "raw";
const RUNG_ORDER: FormatRung[] = ["avif", "webp", "jpeg", "png", "raw"];



/**
 * Premium hero portrait with subtle scroll-driven zoom.
 * - Scales 1.00 → 1.10 as the hero moves through the viewport.
 * - Honors prefers-reduced-motion (no animation).
 * - Uses rAF + passive scroll for smoothness.
 * - Graceful degradation: shows a shimmer skeleton while loading and a
 *   branded gradient card (or fallbackSrc) if the network request fails.
 */
export const HeroPortrait = ({
  src,
  alt,
  decorative = false,
  avifSrc,
  webpSrc,
  avifSrcSet,
  webpSrcSet,
  jpgSrcSet,
  pngSrc,
  pngSrcSet,
  sizes = "(min-width: 1280px) 480px, (min-width: 1024px) 40vw, (min-width: 640px) 70vw, 92vw",
  fallbackSrc,
  placeholderSrc,
  anchorX,
  anchorY,
  priority = true,
  zoom = "var(--hero-zoom, 1)",
  debugLabel = "hero",

}: Props) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [scale, setScale] = useState(1);
  const heroDebug = useHeroDebug();

  // Accessible name resolution. A meaningful portrait that ships with a blank
  // alt is an a11y regression (screen readers fall back to announcing the file
  // name), so we surface it loudly in dev and treat it as decorative at
  // runtime rather than emitting a nameless image.
  const trimmedAlt = (alt ?? "").trim();
  const isDecorative = decorative || trimmedAlt.length === 0;
  const resolvedAlt = isDecorative ? "" : trimmedAlt;

  if (import.meta.env.DEV && !decorative && trimmedAlt.length === 0) {
    console.warn(
      `[HeroPortrait] "${debugLabel}" rendered without alt text. Pass a description ` +
        `(see src/content/image-alt.ts) or set decorative when the image is purely visual.`,
    );
  }



  // A placeholder constant that was never wired up (empty string, undefined,
  // or a leftover `""` export) would render an <img> with no src, which never
  // fires `onError` in some browsers — so we detect "nothing to load" up front
  // and go straight to the branded card instead of showing a blank frame.
  const hasAnySource = Boolean(
    (src && src.trim()) ||
      avifSrcSet ||
      avifSrc ||
      webpSrcSet ||
      webpSrc ||
      jpgSrcSet ||
      pngSrcSet ||
      pngSrc,
  );

  const [status, setStatus] = useState<"loading" | "loaded" | "failed">(
    hasAnySource ? "loading" : "failed",
  );
  // The author-supplied fallbackSrc can 404 too; when it does we drop to the
  // branded initials card rather than leaving a broken-image icon.
  const [fallbackFailed, setFallbackFailed] = useState(false);
  // A missing LQIP must never render as a broken-image icon behind the hero.
  const [placeholderFailed, setPlaceholderFailed] = useState(false);
  // The LQIP stays mounted until its fade-out transition has finished, so the
  // cross-fade never snaps. Unmounting early would flash the dark frame.
  const [placeholderGone, setPlaceholderGone] = useState(false);

  // Re-arm the loader if the caller swaps the image at runtime.
  useEffect(() => {
    setFallbackFailed(false);
    setPlaceholderGone(false);
    setStatus(hasAnySource ? "loading" : "failed");
  }, [src, hasAnySource]);

  // Synchronise the blur placeholder fade-out with the *decoded* frame rather
  // than the load event. `onLoad` fires when bytes are in, which can be several
  // frames before the browser has a paintable bitmap — fading the LQIP there
  // briefly leaves nothing (or a half-painted face) on screen. We await
  // img.decode() (with a rAF fallback for browsers/paths where it rejects) and
  // only then flip to "loaded", which is the same moment the sharp image fades
  // in and the LCP/decoded timing is recorded.
  const markDecoded = useCallback((el: HTMLImageElement | null) => {
    if (!el) return;
    const finish = () => setStatus((s) => (s === "failed" ? s : "loaded"));
    if (typeof el.decode === "function") {
      el.decode()
        .then(finish)
        // decode() rejects on some cross-origin / SVG / detached cases even
        // though the image will paint fine — fall back to the next frame.
        .catch(() => requestAnimationFrame(finish));
    } else {
      requestAnimationFrame(finish);
    }
  }, []);




  // Which rungs are actually available given the props supplied. A rung is
  // "present" if either its srcset or single-URL variant was passed. We keep
  // the raw src as an unconditional last rung because <img src> always exists.
  const rungPresent: Record<FormatRung, boolean> = {
    avif: Boolean(avifSrcSet || avifSrc),
    webp: Boolean(webpSrcSet || webpSrc),
    jpeg: Boolean(jpgSrcSet),
    png: Boolean(pngSrcSet || pngSrc),
    raw: true,
  };

  // Index into RUNG_ORDER. On each <img> onError we advance past the current
  // rung *and* skip any absent rungs, so a project with only AVIF+JPEG jumps
  // straight from avif → jpeg. Once we run out of rungs we flip to the
  // author-supplied fallbackSrc, and finally to the branded initials card.
  const [rungIdx, setRungIdx] = useState<number>(() =>
    RUNG_ORDER.findIndex((r) => rungPresent[r]),
  );
  const activeRung: FormatRung | undefined = RUNG_ORDER[rungIdx];

  // A cached image can already be `complete` before React attaches onLoad —
  // decode it straight away so the blur placeholder still clears.
  useEffect(() => {
    const el = imgRef.current;
    if (el?.complete && el.naturalWidth > 0) markDecoded(el);
  }, [markDecoded, src, activeRung]);

  // Safety net: `transitionend` never fires in a background tab or when the
  // user prefers reduced motion, so retire the blur on a timer too.
  useEffect(() => {
    if (status !== "loaded") return;
    const t = window.setTimeout(() => setPlaceholderGone(true), 400);
    return () => window.clearTimeout(t);
  }, [status]);



  const advanceRung = () => {
    for (let i = rungIdx + 1; i < RUNG_ORDER.length; i++) {
      if (rungPresent[RUNG_ORDER[i]]) {
        setRungIdx(i);
        return;
      }
    }
    // Out of format rungs — fall through to fallbackSrc / branded card.
    setStatus("failed");
  };



  // Instrument LCP-relevant timings for the hero image (start / responseEnd /
  // load / decoded / lcp). Reports via console + `hero-perf` CustomEvent +
  // `window.__heroPerf`. Only active for the LCP instance to avoid noise from
  // QA/below-the-fold placements.
  useEffect(() => {
    if (!priority) return;
    const el = imgRef.current;
    if (!el) return;
    return instrumentHeroImage(el, { label: "hero", requestedSrc: src });
  }, [priority, src]);
  // Derive initials from the alt text for the branded fallback card.
  const initials = alt
    .replace(/\b(portrait|photo|image|of|dr\.?)\b/gi, "")
    .trim()
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase() || "DS";

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;

    let raf = 0;
    const update = () => {
      raf = 0;
      const el = wrapRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      // progress: 0 when image bottom hits viewport top, 1 when image top hits viewport bottom
      const raw = 1 - (rect.top + rect.height / 2) / vh;
      const progress = Math.max(0, Math.min(1, raw));
      // Map to a gentle 1.00 → 1.10 zoom
      setScale(1 + progress * 0.1);
    };

    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div className="relative">
      {/* Cinematic glow halo behind the portrait (brand-tinted, very subtle) */}
      <div
        aria-hidden
        className="pointer-events-none absolute -inset-8 sm:-inset-12 lg:-inset-16 -z-10 blur-3xl opacity-70"
        style={{
          background:
            "radial-gradient(60% 55% at 65% 35%, hsl(var(--primary) / 0.35) 0%, transparent 60%), radial-gradient(55% 50% at 35% 70%, hsl(var(--accent) / 0.22) 0%, transparent 65%)",
        }}
      />

      <div
        ref={wrapRef}
        // Announce the loading state so a screen reader that lands on the hero
        // mid-fetch is told the image is still resolving instead of hearing
        // nothing at all. Decorative instances stay fully hidden.
        aria-busy={!isDecorative && status === "loading" ? true : undefined}
        aria-hidden={isDecorative || undefined}
        className="relative overflow-hidden rounded-md aspect-[4/5] sm:aspect-[4/5] lg:aspect-[5/6] xl:aspect-[4/5]"
        style={{
          // Feather the frame so the (dark-background) portrait dissolves into
          // the hero section instead of reading as a pasted rectangle. Both
          // axes fade independently, then multiply via mask-composite.
          WebkitMaskImage:
            "linear-gradient(to bottom, transparent 0%, #000 9%, #000 84%, transparent 100%), linear-gradient(to right, transparent 0%, #000 8%, #000 92%, transparent 100%)",
          maskImage:
            "linear-gradient(to bottom, transparent 0%, #000 9%, #000 84%, transparent 100%), linear-gradient(to right, transparent 0%, #000 8%, #000 92%, transparent 100%)",
          WebkitMaskComposite: "source-in",
          maskComposite: "intersect",
        }}
      >

        {/* Shimmer skeleton — only shown when no LQIP is available. When a
            `placeholderSrc` is supplied it replaces the shimmer with a real
            blur-up of the hero, which reads far more premium on slow links. */}
        {status === "loading" && (!placeholderSrc || placeholderFailed) && (
          <div
            aria-hidden
            className="absolute inset-0 animate-pulse bg-gradient-to-br from-navy-elevated via-navy to-navy-deep"
          />
        )}

        {/* Blur-up LQIP. Uses the same `objectPosition` as the sharp image so
            the crop anchor is identical — the face never shifts when the full
            asset resolves. The fade-out is driven by the *decoded* event (see
            markDecoded), not onLoad, so the blur can never sit on top of an
            already-painted face. It unmounts only after the fade completes. */}
        {placeholderSrc &&
          !placeholderFailed &&
          status !== "failed" &&
          !placeholderGone && (
          <img
            aria-hidden
            src={placeholderSrc}
            alt=""
            width={1360}
            height={1700}
            // The LQIP is ~1-2 KB, but on a below-the-fold portrait even that
            // shouldn't compete with above-the-fold work — mirror the main
            // image's loading mode so off-screen instances stay deferred.
            // Its fetch priority stays low regardless: on a priority hero the
            // sharp image is the LCP candidate and must win the network race.
            loading={priority ? "eager" : "lazy"}
            fetchPriority="low"
            decoding="async"
            draggable={false}
            onError={() => setPlaceholderFailed(true)}
            onTransitionEnd={(e) => {
              if (e.propertyName === "opacity" && status === "loaded") {
                setPlaceholderGone(true);
              }
            }}
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition:
                `${anchorX ?? "var(--hero-anchor-x, clamp(48%, calc(52% - 0.4vw), 52%))"} ` +
                `${anchorY ?? "var(--hero-anchor-y, clamp(34%, calc(28% + 6vw), 50%))"}`,
              transform: `scale(calc(${zoom} * 1.08))`,
              filter: "blur(24px) saturate(1.1)",
              opacity: status === "loaded" ? 0 : 1,
              // Matches the sharp image's fade-in so the cross-fade is
              // symmetrical — no dark dip, no lingering blur over the face.
              transition: "opacity 320ms ease-out",
              willChange: "opacity",
              pointerEvents: "none",
            }}
          />
        )}


        {status !== "failed" && (
          // `key` forces the browser to re-evaluate <picture> when the active
          // rung changes, so a WebP retry after an AVIF error re-runs source
          // selection and actually fetches the new format instead of caching
          // the failed AVIF decision.
          <picture key={`rung-${activeRung}`}>
            {/* Only emit <source> tags at or after the active rung. When AVIF
                errors we advance the rung and the AVIF <source> is removed
                from the DOM, so the browser can no longer re-pick it. */}
            {activeRung === "avif" && (avifSrcSet || avifSrc) && (
              <source
                type="image/avif"
                srcSet={avifSrcSet ?? avifSrc}
                sizes={avifSrcSet ? sizes : undefined}
              />
            )}
            {(activeRung === "avif" || activeRung === "webp") &&
              (webpSrcSet || webpSrc) && (
                <source
                  type="image/webp"
                  srcSet={webpSrcSet ?? webpSrc}
                  sizes={webpSrcSet ? sizes : undefined}
                />
              )}
            {activeRung !== "png" && activeRung !== "raw" && jpgSrcSet && (
              <source type="image/jpeg" srcSet={jpgSrcSet} sizes={sizes} />
            )}
            {activeRung !== "raw" && (pngSrcSet || pngSrc) && (
              <source
                type="image/png"
                srcSet={pngSrcSet ?? pngSrc}
                sizes={pngSrcSet ? sizes : undefined}
              />
            )}
            <img
              ref={imgRef}
              // Pick the safest matching URL for the <img> fallback slot so
              // browsers that skip every <source> still get a usable asset.
              src={
                activeRung === "png" && pngSrc
                  ? pngSrc
                  : activeRung === "png" && !pngSrc && pngSrcSet
                    ? src
                    : src
              }
              srcSet={
                activeRung === "png"
                  ? pngSrcSet
                  : activeRung === "raw"
                    ? undefined
                    : jpgSrcSet
              }
              sizes={
                (activeRung === "png" && pngSrcSet) ||
                (activeRung !== "raw" && jpgSrcSet)
                  ? sizes
                  : undefined
              }
              alt={resolvedAlt}
              width={1360}
              height={1700}
              loading={priority ? "eager" : "lazy"}
              fetchPriority={priority ? "high" : "low"}
              decoding="async"
              draggable={false}
              onLoad={(e) => markDecoded(e.currentTarget)}
              onError={advanceRung}

              style={{
                // Responsive crop: the CSS `--hero-zoom` multiplier scales the
                // subject inside the frame per breakpoint (the frame aspect
                // changes 4/5 → 5/6 → 4/5), so face + blazer + posture keep the
                // same visual weight on every screen. The JS `scale` adds the
                // scroll-driven parallax zoom on top of it.
                transform: `translate3d(0,0,0) scale(calc(${zoom} * ${scale}))`,
                transition:
                  "transform 200ms cubic-bezier(0.22, 1, 0.36, 1), opacity 320ms ease-out",
                willChange: "transform",
                backfaceVisibility: "hidden",
                WebkitBackfaceVisibility: "hidden",
                WebkitTransform: `translate3d(0,0,0) scale(calc(${zoom} * ${scale}))`,

                opacity: status === "loaded" ? 1 : 0,
                // Balanced grade: lifts the shadow detail in the dark backdrop
                // while keeping the blazer saturated instead of neon.
                filter: "contrast(1.04) saturate(1.06) brightness(1.03)",
                // Face-anchor crop. Reads site-wide CSS variables so it can
                // be retuned from `index.css` (or via per-instance
                // `anchorX`/`anchorY` props) without editing this component.
                objectPosition:
                  `${anchorX ?? "var(--hero-anchor-x, clamp(48%, calc(52% - 0.4vw), 52%))"} ` +
                  `${anchorY ?? "var(--hero-anchor-y, clamp(34%, calc(28% + 6vw), 50%))"}`,
              }}
              className="absolute inset-0 h-full w-full object-cover select-none"

            />
          </picture>
        )}

        {/* Graceful degradation: if every <source> fails on a slow/blocked
            connection, either render an author-provided fallbackSrc or a
            branded gradient card with initials — never a broken-image icon. */}
        {status === "failed" && (
          fallbackSrc && fallbackSrc.trim() && !fallbackFailed ? (
            <img
              src={fallbackSrc}
              alt={resolvedAlt}
              width={1360}
              height={1700}
              loading={priority ? "eager" : "lazy"}
              fetchPriority={priority ? "high" : "low"}
              decoding="async"
              draggable={false}
              onError={() => setFallbackFailed(true)}
              className="absolute inset-0 h-full w-full object-cover select-none"
            />
          ) : (

            <div
              // The branded initials card stands in for the photo, so it keeps
              // the photo's accessible name — unless the portrait is
              // decorative, in which case it stays out of the a11y tree.
              role={isDecorative ? undefined : "img"}
              aria-label={isDecorative ? undefined : resolvedAlt}
              aria-hidden={isDecorative || undefined}
              className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-gradient-to-br from-navy-elevated via-navy to-navy-deep text-center p-6"
            >
              <div
                aria-hidden
                className="font-display text-6xl md:text-7xl font-bold tracking-wider bg-gradient-to-br from-gold via-gold to-orange bg-clip-text text-transparent"
              >
                {initials}
              </div>
              <div
                aria-hidden
                className="text-[10px] uppercase tracking-[0.25em] text-silver/70 max-w-[18ch] leading-relaxed"
              >
                {resolvedAlt || "Portrait unavailable"}
              </div>
            </div>
          )
        )}



        {/* Cinematic vignette: darkens the outer edges only, so the face and
            blazer stay bright while the frame still melts into the section. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(130% 100% at 58% 34%, transparent 58%, hsl(var(--background) / 0.42) 100%)",
          }}
        />

        {/* Warm key light on the face side + cool floor shadow for balance */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 mix-blend-soft-light"
          style={{
            background:
              "radial-gradient(70% 55% at 55% 28%, hsl(var(--accent) / 0.22) 0%, transparent 70%), linear-gradient(180deg, transparent 55%, hsl(var(--background) / 0.35) 100%)",
          }}
        />


        {/* Subtle film-grain overlay (SVG fractal noise, no asset) */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.12] mix-blend-overlay"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.6 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
            backgroundSize: "240px 240px",
          }}
        />

        {/* QA-only crop inspector: anchor crosshair + crop bounds + readout. */}
        {heroDebug && (
          <HeroDebugOverlay frameRef={wrapRef} imgRef={imgRef} label={debugLabel} />
        )}
      </div>
    </div>
  );
};
